import React, { useEffect, useRef, useState } from "react";
import "../Messages/messages.css";
import axios from "axios";
import { BeatLoader } from "react-spinners";
import { MdOutlinePushPin } from "react-icons/md";
import { Tooltip } from "react-tooltip";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import { UilAngleLeftB, UilAngleRightB } from "@iconscout/react-unicons";
import JobCategories from "../JobCategories/JobCategories";

const Jobs = () => {
  const [activeTab, setActiveTab] = useState("applications");
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [images] = useState([
    "/photos/chat-1.svg",
    "/photos/chat-2.svg",
    "/photos/chat-3.svg",
  ]);
  const [randomImage, setRandomImage] = useState("");
  const [pinnedJobs, setPinnedJobs] = useState(
    JSON.parse(localStorage.getItem("pinnedJobs")) || []
  );
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedjobId, setSelectedJobId] = useState(null);
  const [showMenu, setShowMenu] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const menuRef = useRef(null);
  const [lang] = useState(localStorage.getItem("lang") || `"ar"`);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalJobs, setTotalJobs] = useState(0);
  const fileInputRef = useRef(null);
  const [bundleLoading, setBundleLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [jobCategories, setJobCategories] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);

  const token =
    localStorage.getItem("authToken") || sessionStorage.getItem("authToken");

  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      navigate("/dashboard/login");
    }
  }, [token, navigate]);

  useEffect(() => {
    if (!token) return;
    const fetchJobCategories = async () => {
      try {
        const response = await axios.get("/job-categories/all", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setJobCategories(Array.isArray(response?.data) ? response.data : []);
      } catch (error) {
        console.error("Error fetching job categories:", error);
      }
    };
    fetchJobCategories();
  }, [token]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterCategory, filterStatus, startDate, endDate]);

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams({ page: String(currentPage) });
        if (searchTerm) params.set("search", searchTerm);
        if (filterCategory) params.set("category", filterCategory);
        if (filterStatus) params.set("status", filterStatus);
        if (startDate) params.set("startDate", startDate);
        if (endDate) params.set("endDate", endDate);
        const response = await axios.get(`/jobs?${params.toString()}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setJobs(response?.data?.jobs);
        setTotalPages(response?.data?.totalPages);
        setTotalJobs(response?.data?.totalJobs);
        localStorage.setItem("jobs", JSON.stringify(response?.data?.jobs));
      } catch (error) {
        console.error("Error fetching jobs:", error);
      } finally {
        setLoading(false);
      }
    };

    const getRandomImage = () => {
      const randomIndex = Math.floor(Math.random() * images.length);
      return images[randomIndex];
    };

    fetchJobs();
    setRandomImage(getRandomImage());
  }, [images, currentPage, token, searchTerm, filterCategory, filterStatus, startDate, endDate]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(null);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  const handlePinJob = (jobId) => {
    let updatedPinnedJobs;
    if (pinnedJobs.includes(jobId)) {
      updatedPinnedJobs = pinnedJobs.filter((id) => id !== jobId);
    } else {
      updatedPinnedJobs = [jobId, ...pinnedJobs];
    }
    setPinnedJobs(updatedPinnedJobs);
    localStorage.setItem("pinnedJobs", JSON.stringify(updatedPinnedJobs));
    setShowMenu(false);
  };

  const handleToggleReadJob = async (job) => {
    try {
      const updatedIsRead = !job?.isRead;

      await axios.put(
        `/jobs/read-job/${job?._id}`,
        {
          isRead: updatedIsRead,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setJobs((prevJobs) =>
        prevJobs.map((jb) =>
          jb._id === job._id ? { ...jb, isRead: updatedIsRead } : jb
        )
      );

      setShowMenu(false);
      toast.success("The job has been read..!");
    } catch (error) {
      toast.error("Failed to update the job status.");
    }
  };

  const handleStatusChange = async (job, newStatus) => {
    try {
      await axios.put(
        `/jobs/read-job/${job._id}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setJobs((prev) =>
        prev.map((jb) =>
          jb._id === job._id
            ? { ...jb, status: newStatus, isRead: newStatus !== "pending" ? true : jb.isRead }
            : jb
        )
      );
      toast.success("Status updated.");
    } catch (error) {
      toast.error("Failed to update status.");
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleExportExcel = async () => {
    try {
      setExportLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.set("search", searchTerm);
      if (filterCategory) params.set("category", filterCategory);
      if (filterStatus) params.set("status", filterStatus);
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);
      const qs = params.toString();
      const res = await axios.get(`/jobs/export/excel${qs ? `?${qs}` : ""}`, {
        responseType: "blob",
        headers: { Authorization: `Bearer ${token}` },
      });
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^";]+)"?/i);
      const filename =
        match?.[1]?.trim() || `jobs-export-${new Date().toISOString().slice(0, 10)}.xlsx`;
      downloadBlob(res.data, filename);
      toast.success("Excel download started.");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not export jobs to Excel.");
    } finally {
      setExportLoading(false);
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const confirmDelete = (jobId) => {
    setShowMenu(false);
    setSelectedJobId(jobId);
    setShowConfirmation(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`/jobs/${selectedjobId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setJobs(jobs.filter((job) => job._id !== selectedjobId));
      setPinnedJobs(pinnedJobs.filter((id) => id !== selectedjobId));
      localStorage.setItem(
        "pinnedJobs",
        JSON.stringify(pinnedJobs.filter((id) => id !== selectedjobId))
      );
      setShowConfirmation(false);
      setSelectedJobId(null);
      toast.success("The job has been deleted..!");
    } catch (error) {
      console.error("Error deleting job:", error);
    }
  };

  const handleMenuClick = (event, messageId) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setMenuPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    });
    setShowMenu(showMenu === messageId ? null : messageId);
  };

  const sortedJobs = [
    ...jobs.filter((job) => pinnedJobs.includes(job?._id)),
    ...jobs
      .filter((job) => !pinnedJobs.includes(job._id))
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
  ];

  const handleDownloadBundle = async () => {
    try {
      setBundleLoading(true);
      const res = await axios.get("/content/join-contact/bundle", {
        responseType: "blob",
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "join-contact-backup.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded.");
    } catch (error) {
      toast.error(error.response?.data?.message || "Backup download failed.");
    } finally {
      setBundleLoading(false);
    }
  };

  const handleImportSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setBundleLoading(true);
      const form = new FormData();
      form.append("bundle", file);
      await axios.post("/content/join-contact/import", form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Backup imported.");
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.message || "Import failed.");
    } finally {
      setBundleLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div className="messages-page">
      <div className="container">
        <div className="dash-tabs">
          <button
            type="button"
            className={`dash-tab-btn ${activeTab === "applications" ? "active" : ""}`}
            onClick={() => setActiveTab("applications")}
          >
            Applications
          </button>
          <button
            type="button"
            className={`dash-tab-btn ${activeTab === "categories" ? "active" : ""}`}
            onClick={() => setActiveTab("categories")}
          >
            Categories
          </button>
        </div>

        {activeTab === "categories" ? (
          <JobCategories token={token} />
        ) : loading ? (
          <div className="loader-container">
            <BeatLoader />
          </div>
        ) : (
        <>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <h1 style={{ margin: 0 }}>Jobs {`(${totalJobs})`}</h1>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <button type="button" className="dash-btn" onClick={handleDownloadBundle} disabled={bundleLoading}>
              {bundleLoading ? "Working…" : "Download backup (Join Us + Contact)"}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".zip"
              onChange={handleImportSelected}
              disabled={bundleLoading}
            />
          </div>
        </div>
        <div
          style={{
            display: "flex",
            gap: 10,
            alignItems: "center",
            flexWrap: "wrap",
            margin: "14px 0",
            padding: "12px 14px",
            background: "#f8f6f4",
            borderRadius: 12,
            border: "1px solid rgba(154, 116, 98, 0.18)",
          }}
        >
          <input
            type="text"
            placeholder="Search by name or email…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid rgba(154,116,98,0.3)", minWidth: 200 }}
          />
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid rgba(154,116,98,0.3)" }}
          >
            <option value="">All categories</option>
            {jobCategories.map((cat) => (
              <option key={cat._id} value={cat.label}>
                {cat.label}
              </option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid rgba(154,116,98,0.3)" }}
          >
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="reviewed">Reviewed</option>
            <option value="interviewed">Interviewed</option>
            <option value="rejected">Rejected</option>
            <option value="hired">Hired</option>
          </select>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.85rem", color: "#6b5344", fontWeight: 600 }}>
            From
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{ padding: "6px 8px", borderRadius: 8, border: "1px solid rgba(154,116,98,0.3)" }}
            />
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.85rem", color: "#6b5344", fontWeight: 600 }}>
            To
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{ padding: "6px 8px", borderRadius: 8, border: "1px solid rgba(154,116,98,0.3)" }}
            />
          </label>
          <button type="button" className="dash-btn dash-btn-primary" onClick={handleExportExcel} disabled={exportLoading}>
            {exportLoading ? "Exporting..." : "Download Excel"}
          </button>
        </div>
        <div className="boxes">
          {sortedJobs?.map((job) => (
            <div
              key={job._id}
              className={`box ${pinnedJobs.includes(job._id) ? "pinned" : ""}`}
            >
              <div className="box-header">
                <i
                  className="uil uil-ellipsis-v"
                  onClick={(event) => handleMenuClick(event, job._id)}
                ></i>

                {showMenu === job._id && (
                  <div
                    className="menu"
                    ref={menuRef}
                    style={{
                      position: "fixed",
                      top: `${menuPosition.top}px`,
                      left: `${menuPosition.left}px`,
                    }}
                  >
                    <button onClick={() => handlePinJob(job._id)}>
                      {pinnedJobs.includes(job._id) ? "Unpin" : "Pin"} Job
                    </button>
                    <button onClick={() => handleToggleReadJob(job)}>
                      Mark as {job?.isRead === true ? "unread" : "read"}
                    </button>
                    <button onClick={() => confirmDelete(job._id)}>
                      Delete
                    </button>
                  </div>
                )}
                <div className="actions-icons">
                  <select
                    className="job-status-select"
                    value={job.status || "pending"}
                    onChange={(e) => handleStatusChange(job, e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <option value="pending">Pending</option>
                    <option value="reviewed">Reviewed</option>
                    <option value="interviewed">Interviewed</option>
                    <option value="rejected">Rejected</option>
                    <option value="hired">Hired</option>
                  </select>
                  {job?.isRead === true && (
                    <i
                      className="uil uil-eye read-icon"
                      onClick={() => handleToggleReadJob(job)}
                    ></i>
                  )}
                  <MdOutlinePushPin
                    className={`pin ${
                      pinnedJobs.includes(job._id) ? "active" : ""
                    }`}
                    onClick={() => handlePinJob(job._id)}
                  />
                  <i className="uil uil-info-circle" id="job-date"></i>
                  <Tooltip
                    anchorSelect="#job-date"
                    content={`${new Date(job.createdAt).getDate()}/${
                      new Date(job.createdAt).getMonth() + 1
                    }/${new Date(job.createdAt).getFullYear()}`}
                    place="top"
                  />
                </div>
              </div>
              <div className="box-content">
                <div className="box-image">
                  <img src={randomImage} alt="message" />
                </div>
                <h3>{job?.name}</h3>
                <div className="message-data">
                  <div className="email">
                    <p>Email:</p>
                    <p>{job?.email}</p>
                  </div>
                  <div className="phone">
                    <p>Job:</p>
                    <p>{job?.job}</p>
                  </div>
                </div>
                <hr />
                <p className="message">{job?.message}</p>
                <Link to={job?.cv} target="_blank" className="file-btn">
                  Show CV <i className="uil uil-eye"></i>
                </Link>
              </div>
            </div>
          ))}
        </div>
        <div className="pagination" dir="ltr">
          {currentPage >= 1 && (
            <div
              onClick={() => handlePageChange(currentPage - 1)}
              className="active"
            >
              {lang === "en" ? (
                <UilAngleRightB size={32} color="white" />
              ) : (
                <UilAngleLeftB size={32} color="white" />
              )}
            </div>
          )}

          {Array.from({ length: totalPages }, (_, index) => index + 1)
            .filter((pageNumber) => {
              if (pageNumber === 1 || pageNumber === totalPages) return true;
              if (
                pageNumber >= currentPage - 1 &&
                pageNumber <= currentPage + 1
              ) {
                return true;
              }
              return false;
            })
            .map((pageNumber, index, filteredPages) => (
              <>
                {index > 0 && pageNumber !== filteredPages[index - 1] + 1 && (
                  <span className="dots">...</span>
                )}
                <div
                  key={pageNumber}
                  onClick={() => handlePageChange(pageNumber)}
                  className={currentPage === pageNumber ? "active" : ""}
                >
                  {pageNumber}
                </div>
              </>
            ))}

          {currentPage < totalPages && (
            <div
              onClick={() => handlePageChange(currentPage + 1)}
              className="active"
            >
              {lang === `"ar"` ? (
                <UilAngleRightB size={32} color="white" />
              ) : (
                <UilAngleLeftB size={32} color="white" />
              )}
            </div>
          )}
        </div>
        </>
        )}
      </div>
      {showConfirmation && (
        <div className="confirmation-modal">
          <div className="modal-content sec">
            <img src="/photos/question.svg" alt="?" />
            <p>Are you sure you want to delete this job?</p>
            <div className="modal-actions">
              <button onClick={handleDelete}>Yes</button>
              <button onClick={() => setShowConfirmation(false)}>No</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Jobs;
